package io.horion.auth.presentation.ui.components.auth

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun AuthTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    type: InputType<String> = InputType.Text,
    leadingIcon: @Composable () -> Unit = {},
    error: String? = null,
    success: Boolean = false,
    enabled: Boolean = true,
    modifier: Modifier = Modifier
) {
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)

    var isFocused by remember { mutableStateOf(false) }
    var showPassword by remember { mutableStateOf(false) }

    Column(modifier = modifier) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .backgroundColor(colors.inputBg)
                .border(
                    width = 2.px,
                    style = LineStyle.Solid,
                    color = when {
                        !enabled      -> colors.borderLight
                        error != null -> colors.error
                        success       -> colors.success
                        isFocused     -> colors.primary
                        else          -> colors.border
                    }
                )
                .styleModifier {
                    if (!enabled) property("opacity", "0.5")
                    property("cursor", if (!enabled) "not-allowed" else "text")
                }
                .transition(Transition.of("all", 0.2.s, AnimationTimingFunction.EaseOut))
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(14.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(modifier = Modifier.padding(right = 12.px)) {
                    leadingIcon()
                }

                Input(
                    type = if (type == InputType.Password && showPassword) InputType.Text else type,
                    attrs = {
                        value(value)
                        onInput { if (enabled) onValueChange(it.value) }
                        onFocusIn  { if (enabled) isFocused = true }
                        onFocusOut { isFocused = false }
                        attr("placeholder", placeholder)
                        if (!enabled) attr("disabled", "true")
                        style {
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("color", colors.onBackground.toString())
                            property("flex", "1")
                            property("font-size", "16px")
                            property("width", "100%")
                        }
                    }
                )

                if (type == InputType.Password) {
                    if (showPassword) {
                        FaEyeSlash(
                            modifier = Modifier
                                .color(colors.primary)
                                .cursor(Cursor.Pointer)
                                .onClick { showPassword = false }
                        )
                    } else {
                        FaEye(
                            modifier = Modifier
                                .color(colors.secondary)
                                .cursor(Cursor.Pointer)
                                .onClick { showPassword = true }
                        )
                    }
                }

                if (success) {
                    FaCheck(modifier = Modifier.color(colors.success))
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .color(colors.error)
                    .fontSize(AuthTypography.tiny)
                    .padding(top = 6.px, left = 12.px)
            )
        }
    }
}